import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons'
import {
	Button,
	Card,
	Descriptions,
	Input,
	Popconfirm,
	Select,
	Space,
	Table,
	Tabs,
	Tag,
	Typography,
	message,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import { ROUTE } from '../../utils/constant'
import './EmailSendJobDetail.scss'

const { Text } = Typography
const { TabPane } = Tabs

const STATUS_COLOR = {
	queued: 'gold',
	running: 'processing',
	completed: 'success',
	failed: 'error',
	cancelled: 'default',
	sent: 'success',
	skipped: 'default',
	delivered: 'cyan',
	bounced: 'error',
	complained: 'magenta',
}

const CANCELLABLE = new Set(['queued', 'running'])

const formatTime = value => {
	if (!value) return '—'
	try {
		return new Date(value).toLocaleString()
	} catch {
		return String(value)
	}
}

export const EmailSendJobDetail = () => {
	const history = useHistory()
	const { jobId } = useParams()
	const [loading, setLoading] = useState(false)
	const [job, setJob] = useState(null)
	const [recipients, setRecipients] = useState([])
	const [recipientTotal, setRecipientTotal] = useState(0)
	const [recipientLoading, setRecipientLoading] = useState(false)
	const [page, setPage] = useState(0)
	const [pageSize, setPageSize] = useState(20)
	const [status, setStatus] = useState(undefined)
	const [query, setQuery] = useState('')
	const [isCancelling, setIsCancelling] = useState(false)

	const loadJob = useCallback(async () => {
		if (!jobId) return
		setLoading(true)
		try {
			const rs = await api.emailAdminApi.getSendJobDetail(jobId)
			setJob(rs)
		} catch (err) {
			message.error(err?.message || 'Could not load job detail.')
		} finally {
			setLoading(false)
		}
	}, [jobId])

	const loadRecipients = useCallback(async () => {
		if (!jobId) return
		setRecipientLoading(true)
		try {
			const rs = await api.emailAdminApi.listSendJobRecipients(jobId, {
				page,
				pageSize,
				status,
				q: query,
			})
			setRecipients(Array.isArray(rs?.data) ? rs.data : [])
			setRecipientTotal(Number(rs?.total) || 0)
		} catch (err) {
			message.error(err?.message || 'Could not load recipients.')
		} finally {
			setRecipientLoading(false)
		}
	}, [jobId, page, pageSize, status, query])

	useEffect(() => {
		loadJob()
	}, [loadJob])

	useEffect(() => {
		loadRecipients()
	}, [loadRecipients])

	const handleCancelJob = useCallback(async () => {
		if (!jobId) return
		setIsCancelling(true)
		try {
			const rs = await api.emailAdminApi.cancelSendJob(jobId)
			setJob(prev => (prev ? { ...prev, ...rs } : rs))
			message.success('Cancel request sent.')
			await loadJob()
			await loadRecipients()
		} catch (err) {
			message.error(err?.message || 'Cancel failed.')
		} finally {
			setIsCancelling(false)
		}
	}, [jobId, loadJob, loadRecipients])

	const counts = job?.recipientCounts
	const result = job?.result
	const canCancel = CANCELLABLE.has(job?.status)
	const displayJobID = job?.jobID || jobId || ''

	const recipientColumns = [
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			ellipsis: true,
		},
		{
			title: 'User ID',
			dataIndex: 'userId',
			key: 'userId',
			width: 100,
			render: value => value ?? '—',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 110,
			render: value => (
				<Tag color={STATUS_COLOR[value] || 'default'}>{value}</Tag>
			),
		},
		{
			title: 'Sent at',
			dataIndex: 'sentAt',
			key: 'sentAt',
			width: 160,
			render: formatTime,
		},
		{
			title: 'Delivered',
			dataIndex: 'deliveredAt',
			key: 'deliveredAt',
			width: 160,
			render: formatTime,
		},
		{
			title: 'Bounced',
			dataIndex: 'bouncedAt',
			key: 'bouncedAt',
			width: 160,
			render: formatTime,
		},
		{
			title: 'Opened',
			key: 'opened',
			width: 120,
			render: (_, record) =>
				record.openedAt
					? `${formatTime(record.openedAt)} (${record.openCount || 1})`
					: '—',
		},
		{
			title: 'Error',
			dataIndex: 'errorMessage',
			key: 'errorMessage',
			ellipsis: true,
			render: value => value || '—',
		},
	]

	return (
		<ListPageShell className="email-send-job-detail">
			<div className="email-send-job-detail__header">
				<Button
					icon={<ArrowLeftOutlined />}
					onClick={() =>
						history.push(
							`${ROUTE.EMAIL_CONTENT_MANAGEMENT}${ROUTE.EMAIL_SEND_HISTORY}`
						)
					}
				>
					Back
				</Button>
				<div className="email-send-job-detail__title-row">
					<ListPageIntro
						icon={MailOutlined}
						title="Send job detail"
						subtitle={displayJobID || ''}
					/>
					{canCancel ? (
						<Popconfirm
							title="Cancel this send job? Sending will stop after the current recipient."
							okText="Cancel job"
							cancelText="Keep sending"
							okButtonProps={{ danger: true, loading: isCancelling }}
							onConfirm={handleCancelJob}
						>
							<Button danger loading={isCancelling}>
								Cancel job
							</Button>
						</Popconfirm>
					) : null}
				</div>
			</div>

			<Card loading={loading} bordered className="email-send-job-detail__card">
				{job && (
					<Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
						<Descriptions.Item label="Job ID" span={3}>
							<Text
								copyable={{
									text: displayJobID,
									tooltips: ['Copy job ID', 'Copied'],
								}}
								strong
							>
								{displayJobID}
							</Text>
						</Descriptions.Item>
						<Descriptions.Item label="Status">
							<Tag color={STATUS_COLOR[job.status] || 'default'}>
								{job.status}
							</Tag>
						</Descriptions.Item>
						<Descriptions.Item label="Subject">{job.subject || '—'}</Descriptions.Item>
						<Descriptions.Item label="Template">
							{job.templateName ||
								(job.templateId ? `#${job.templateId}` : '—')}
						</Descriptions.Item>
						<Descriptions.Item label="Type">{job.emailType}</Descriptions.Item>
						<Descriptions.Item label="Scope">
							{job.scope === 'single_user'
								? job.targetEmail || '1 user'
								: 'All users'}
						</Descriptions.Item>
						<Descriptions.Item label="Created">
							{formatTime(job.createdAt)}
						</Descriptions.Item>
						<Descriptions.Item label="Started">
							{formatTime(job.startedAt)}
						</Descriptions.Item>
						<Descriptions.Item label="Finished">
							{formatTime(job.finishedAt)}
						</Descriptions.Item>
						<Descriptions.Item label="Recipients">
							{counts
								? `${counts.sent} sent / ${counts.failed} failed / ${counts.skipped} skipped (total ${counts.total})`
								: result
									? `${result.emailsSent || 0} sent / ${result.emailsFailed || 0} failed / ${result.emailsSkipped || 0} skipped`
									: '—'}
						</Descriptions.Item>
						<Descriptions.Item label="Engagement">
							{counts
								? `${counts.delivered || 0} delivered / ${counts.bounced || 0} bounced / ${counts.complained || 0} complained / ${counts.opened || 0} opened / ${counts.clicked || 0} clicked`
								: '—'}
						</Descriptions.Item>
						{job.error ? (
							<Descriptions.Item label="Error" span={3}>
								<Text type="danger">{job.error}</Text>
							</Descriptions.Item>
						) : null}
					</Descriptions>
				)}
			</Card>

			<Tabs className="email-send-job-detail__tabs" defaultActiveKey="recipients">
				<TabPane tab="Recipients" key="recipients">
					<div className="email-send-job-detail__toolbar">
						<Space wrap>
							<Input.Search
								allowClear
								placeholder="Filter by email"
								onSearch={value => {
									setPage(0)
									setQuery(value || '')
								}}
								style={{ width: 240 }}
							/>
							<Select
								allowClear
								placeholder="Status"
								style={{ width: 140 }}
								value={status}
								onChange={value => {
									setPage(0)
									setStatus(value)
								}}
								options={[
									{ value: 'sent', label: 'Sent' },
									{ value: 'failed', label: 'Failed' },
									{ value: 'skipped', label: 'Skipped' },
									{ value: 'delivered', label: 'Delivered' },
									{ value: 'bounced', label: 'Bounced' },
									{ value: 'complained', label: 'Complained' },
									{ value: 'queued', label: 'Queued' },
								]}
							/>
							<Button onClick={loadRecipients}>Refresh</Button>
						</Space>
					</div>
					<Table
						rowKey="id"
						loading={recipientLoading}
						columns={recipientColumns}
						dataSource={recipients}
						pagination={{
							current: page + 1,
							pageSize,
							total: recipientTotal,
							showSizeChanger: true,
							onChange: (nextPage, nextSize) => {
								setPage(nextPage - 1)
								setPageSize(nextSize)
							},
						}}
					/>
				</TabPane>
				<TabPane tab="Content" key="content">
					<div className="email-send-job-detail__content">
						<div className="email-send-job-detail__preview-label">
							HTML preview
						</div>
						<div
							className="email-send-job-detail__preview"
							dangerouslySetInnerHTML={{
								__html: job?.htmlBody || '<em>No HTML body</em>',
							}}
						/>
						{job?.textBody ? (
							<>
								<div className="email-send-job-detail__preview-label">
									Text body
								</div>
								<pre className="email-send-job-detail__text">
									{job.textBody}
								</pre>
							</>
						) : null}
					</div>
				</TabPane>
			</Tabs>
		</ListPageShell>
	)
}
